/**
 * Base Axios Client Configuration
 *
 * This module provides the core axios instance configuration with base settings,
 * environment-based URLs, timeout configuration, and default headers.
 * It uses the new unified integration layer instead of traditional interceptors.
 */

import axios, { type AxiosInstance } from 'axios';

import {
  setupIntegrationLayer,
  createProductionIntegrationConfig,
  createDevelopmentIntegrationConfig,
  createTestIntegrationConfig,
} from './integration';
import { stampTimeZone } from './timezone';
import { API_CONFIG } from '../../config/constants';
import { getAuthConfig, getTokenStorage } from '../../di/container';
import { dayjs } from '../../utils/date';
import { getEnvSafe, appEnv } from '../../utils/env';

import type { NodeEnvironment, PaginatedResponse } from '../../types';
import type {
  UnifiedIntegration,
  UnifiedIntegrationConfig,
  IRequestCoordinator,
} from '../../types/api';
import type {
  ApiClientConfig,
  FileUploadResponse,
  ExtendedApiClient,
} from '../../types/api/client.types';
import type { AuthMode } from '../../types/auth';

// =============================================================================
// DEFAULT CONFIGURATION
// =============================================================================

const DEFAULT_CONFIG: Required<Omit<ApiClientConfig, 'integration'>> = {
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true, // Important for Laravel Sanctum stateful authentication
  withXSRFToken: true, // Important for Laravel Sanctum stateful authentication
  enableLogging: appEnv('development'),
  environment: getEnvSafe('NODE_ENV', 'development') as NodeEnvironment,
};

// =============================================================================
// CLIENT FACTORY FUNCTION
// =============================================================================

/**
 * Creates a configured axios instance with the unified integration layer
 *
 * @param config - Optional configuration to override defaults
 * @returns Object containing the configured axios instance, integration layer, and coordinator
 */
export function createApiClient(config: ApiClientConfig = {}): {
  client: AxiosInstance;
  integration: UnifiedIntegration;
  coordinator: IRequestCoordinator;
} {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  // Create the axios instance
  const client = axios.create({
    baseURL: finalConfig.baseURL,
    timeout: finalConfig.timeout,
    withCredentials: finalConfig.withCredentials,
    withXSRFToken: finalConfig.withXSRFToken,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // Laravel expects this for AJAX requests
    },
  });

  // The device's timezone, so the API reads "today" as the person's day.
  client.interceptors.request.use(stampTimeZone);

  // Platform auth signing. The defaults above are Laravel Sanctum *stateful*
  // (cookies + XSRF). When the platform registered TOKEN auth (mobile, via
  // `registerAuthConfig`), every request must instead carry the bearer PAT and
  // drop the cookie/XSRF transport: React Native has no `document.cookie` to
  // mirror `XSRF-TOKEN` into the `X-XSRF-TOKEN` header, so leaving cookies on
  // would 419 CSRF-checked routes. Web (session mode — or before any config is
  // registered) is unaffected; this interceptor is a no-op there. Without this,
  // every `CrudService` request on mobile went out unauthenticated → 401.
  client.interceptors.request.use(async (requestConfig) => {
    let mode: AuthMode | undefined;
    try {
      mode = getAuthConfig().mode;
    } catch {
      // No platform auth config registered → keep the stateful defaults.
      return requestConfig;
    }

    if (mode !== 'token') return requestConfig;

    requestConfig.withCredentials = false;
    requestConfig.withXSRFToken = false;

    try {
      const token = await getTokenStorage().getAccessToken();
      if (token) {
        // The integration layer's metadata interceptor runs first and rebuilds
        // `headers` as a PLAIN object (to stamp X-Request-ID), so by the time
        // this runs `headers.set` may no longer exist. Assign it in whichever
        // shape `headers` currently is, rather than assuming an AxiosHeaders.
        const headers = requestConfig.headers as {
          set?: (name: string, value: string) => void;
        } & Record<string, string>;
        if (typeof headers.set === 'function') {
          headers.set('Authorization', `Bearer ${token}`);
        } else {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch {
      // No token storage / no token yet → send unauthenticated (e.g. the login
      // screen before a session exists); the 401 is handled upstream.
    }

    return requestConfig;
  });

  // Determine integration configuration based on environment. Under test the
  // default client does not retry, so a mocked 500 fails at once rather than
  // waiting out real backoff; tests of retry behaviour pass their own config.
  const integrationConfig =
    config.integration ||
    (finalConfig.environment === 'production'
      ? createProductionIntegrationConfig()
      : finalConfig.environment === 'test'
        ? createTestIntegrationConfig()
        : createDevelopmentIntegrationConfig());

  // Setup the unified integration layer
  const integration = setupIntegrationLayer(client, integrationConfig);

  // Get the internal coordinator from the integration layer (the one actually handling requests)
  const coordinator = integration.axios.getCoordinator();

  // Log client creation in development
  if (finalConfig.enableLogging) {
    console.log('🔧 API Client created with unified integration:', {
      baseURL: finalConfig.baseURL,
      timeout: finalConfig.timeout,
      withCredentials: finalConfig.withCredentials,
      environment: finalConfig.environment,
      integrationEnabled: true,
      coordinatorEnabled: true,
    });
  }

  return { client, integration, coordinator };
}

// =============================================================================
// DEFAULT CLIENT INSTANCE
// =============================================================================

/**
 * Default API client + integration layer — LAZILY initialized.
 *
 * The whole API stack (axios client + unified integration + coordinator +
 * dedup/tracker cleanup timers) used to be constructed at module-eval time, so
 * importing anything from the shared barrel built it — and started its cleanup
 * timers — immediately. Those timers are `unref`'d, but `unref` is a no-op on
 * React Native (Hermes), so on mobile they ran from app startup regardless. The
 * stack is now built on FIRST USE instead.
 *
 * `apiClient`, `defaultIntegration` and `defaultCoordinator` are exported as
 * transparent lazy proxies, so every existing `import { apiClient }` +
 * `apiClient.get(...)` call site (and `services.api`) keeps working unchanged —
 * the underlying stack is created the first time any property is read or the
 * client is invoked, not at import.
 */
let lazyStack: {
  client: ExtendedApiClient;
  integration: UnifiedIntegration;
  coordinator: IRequestCoordinator;
} | null = null;

function ensureApiStack(): {
  client: ExtendedApiClient;
  integration: UnifiedIntegration;
  coordinator: IRequestCoordinator;
} {
  if (lazyStack) {
    return lazyStack;
  }

  const { client, integration, coordinator } = createApiClient({
    environment: getEnvSafe('NODE_ENV', 'development') as NodeEnvironment,
    enableLogging: appEnv('development'),
  });

  // Extend the client with specialized convenience methods.
  const extended = client as ExtendedApiClient;

  extended.getPaginated = async function (
    url: string,
    params?: Record<string, unknown>
  ): Promise<PaginatedResponse<unknown>> {
    const response = await this.get(url, { params });
    return response.data;
  };

  extended.uploadFile = async function (
    url: string,
    file: File,
    onUploadProgress?: (progress: number) => void
  ): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: { loaded: number; total?: number }) => {
        if (onUploadProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onUploadProgress(progress);
        }
      },
    });

    return {
      data: response.data || null,
      success: response.status >= 200 && response.status < 300,
      message: response.statusText,
      status: response.status,
    };
  };

  lazyStack = { client: extended, integration, coordinator };
  return lazyStack;
}

/**
 * Eagerly construct the API stack now. Optional — call at startup if you want
 * the (small) construction cost paid up front rather than on the first request.
 */
export function initApiClient(): void {
  ensureApiStack();
}

/**
 * Wraps a lazily-resolved target in a fully transparent proxy: property access,
 * mutation, invocation, AND introspection (descriptors, own keys, prototype)
 * all resolve the real instance on first touch. Forwarding the descriptor traps
 * — not just get/set/has/apply — is what keeps reflective tooling such as
 * Object.keys / hasOwnProperty / instanceof / vi.spyOn working against the proxy.
 */
export function createLazyProxy<T extends object>(
  resolve: () => T,
  callable: boolean
): T {
  const target = (callable ? (): void => undefined : {}) as unknown as T;
  return new Proxy(target, {
    get(_t, prop): unknown {
      // Return the raw value without re-binding: axios instance methods are
      // already bound to their internal context, and NOT binding preserves
      // function identity so test spies (vi.fn / spyOn on e.g. apiClient.get)
      // are still recognized as spies.
      return Reflect.get(resolve(), prop);
    },
    set(_t, prop, value): boolean {
      return Reflect.set(resolve(), prop, value);
    },
    has(_t, prop): boolean {
      return Reflect.has(resolve(), prop);
    },
    deleteProperty(_t, prop): boolean {
      return Reflect.deleteProperty(resolve(), prop);
    },
    ownKeys(_t): (string | symbol)[] {
      return Reflect.ownKeys(resolve());
    },
    getOwnPropertyDescriptor(_t, prop): PropertyDescriptor | undefined {
      // Forward descriptor reads so introspection (Object.keys / hasOwnProperty)
      // and reflective tooling (vi.spyOn, which reads a descriptor then redefines
      // it) see the real instance rather than the empty stub target. Force
      // `configurable: true`: the Proxy invariant forbids reporting a
      // non-configurable descriptor for a property the (empty, extensible) target
      // lacks, which would otherwise throw a TypeError.
      const descriptor = Reflect.getOwnPropertyDescriptor(resolve(), prop);
      if (descriptor) {
        descriptor.configurable = true;
      }
      return descriptor;
    },
    defineProperty(_t, prop, descriptor): boolean {
      // configurable descriptors only (vi.spyOn uses configurable: true). A
      // non-configurable descriptor would violate the Proxy invariant against
      // the empty stub target; no caller defines non-configurable props on these
      // singletons, so that case is intentionally unsupported.
      return Reflect.defineProperty(resolve(), prop, descriptor);
    },
    getPrototypeOf(_t): object | null {
      return Reflect.getPrototypeOf(resolve());
    },
    setPrototypeOf(_t, proto): boolean {
      return Reflect.setPrototypeOf(resolve(), proto);
    },
    preventExtensions(): boolean {
      // Refuse rather than forward. Forwarding would return true while the stub
      // target stays extensible (a Proxy invariant violation), and making the
      // stub non-extensible would make the ownKeys trap — which returns the real
      // instance's keys — throw on every later enumeration, permanently bricking
      // the proxy. Returning false makes Object.freeze/seal fail cleanly without
      // corrupting the proxy; freezing a lazy singleton proxy isn't supported.
      // ([[Construct]] is likewise intentionally not forwarded — `new apiClient()`
      // is a bug, and the stub correctly rejects it.)
      return false;
    },
    apply(_t, thisArg, args: unknown[]): unknown {
      // Forward thisArg via Reflect.apply to preserve call semantics (e.g. if
      // the client is ever invoked via .call/.apply). Consistent with the
      // Reflect-based get/set/has traps above.
      const fn = resolve() as unknown as (...a: unknown[]) => unknown;
      return Reflect.apply(fn, thisArg, args);
    },
  });
}

// Typed as AxiosInstance to preserve the original public type of this export
// (the extended getPaginated/uploadFile methods exist at runtime via the proxy).
const apiClient = createLazyProxy<AxiosInstance>(
  () => ensureApiStack().client,
  true
);
const defaultIntegration = createLazyProxy<UnifiedIntegration>(
  () => ensureApiStack().integration,
  false
);
const defaultCoordinator = createLazyProxy<IRequestCoordinator>(
  () => ensureApiStack().coordinator,
  false
);

export { apiClient, defaultIntegration, defaultCoordinator };

// =============================================================================
// CLIENT CONFIGURATION UTILITIES
// =============================================================================

/**
 * Updates the base URL for the default client
 *
 * @param baseURL - New base URL
 */
export function updateBaseURL(baseURL: string): void {
  apiClient.defaults.baseURL = baseURL;
}

/**
 * Updates the timeout for the default client
 *
 * @param timeout - New timeout in milliseconds
 */
export function updateTimeout(timeout: number): void {
  apiClient.defaults.timeout = timeout;
}

/**
 * Gets the current configuration of the default client
 *
 * @returns Current client configuration
 */
export function getClientConfig(): {
  baseURL: string | undefined;
  timeout: number;
  withCredentials: boolean;
  withXSRFToken: boolean;
} {
  return {
    baseURL: apiClient.defaults.baseURL,
    timeout: apiClient.defaults.timeout || DEFAULT_CONFIG.timeout,
    withCredentials:
      typeof apiClient.defaults.withCredentials === 'boolean'
        ? apiClient.defaults.withCredentials
        : false,
    withXSRFToken:
      typeof apiClient.defaults.withXSRFToken === 'boolean'
        ? apiClient.defaults.withXSRFToken
        : false,
  };
}

/**
 * Sets a default header for all requests
 *
 * @param name - Header name
 * @param value - Header value
 */
export function setDefaultHeader(name: string, value: string): void {
  apiClient.defaults.headers.common[name] = value;
}

/**
 * Removes a default header
 *
 * @param name - Header name to remove
 */
export function removeDefaultHeader(name: string): void {
  delete apiClient.defaults.headers.common[name];
}

// =============================================================================
// INTEGRATION MANAGEMENT
// =============================================================================

/**
 * Get the default integration layer for monitoring and debugging
 *
 * @returns Default integration layer
 */
export function getDefaultIntegration(): UnifiedIntegration {
  return defaultIntegration;
}

/**
 * Get comprehensive metrics from all integration components
 *
 * @returns Combined metrics from all integration layers
 */
export function getIntegrationMetrics(): {
  axios: ReturnType<typeof defaultIntegration.axios.getMetrics>;
  performance: ReturnType<
    typeof defaultIntegration.performance.getPerformanceSnapshot
  >;
} {
  return defaultIntegration.getMetrics();
}

/**
 * Update integration configuration
 *
 * @param config - New integration configuration
 */
export function updateIntegrationConfig(
  config: Partial<UnifiedIntegrationConfig>
): void {
  if (config.axios) {
    defaultIntegration.axios.updateConfig(config.axios);
  }
  if (config.auth) {
    defaultIntegration.auth.updateConfig(config.auth);
  }
  if (config.performance) {
    defaultIntegration.performance.updateConfig(config.performance);
  }
}

// =============================================================================
// HEALTH CHECK UTILITY
// =============================================================================

/**
 * Performs a health check against the API with integration metrics
 *
 * @returns Promise resolving to health check result with integration data
 */
export async function healthCheck(): Promise<{
  status: 'healthy' | 'unhealthy';
  responseTime: number;
  timestamp: string;
  integration: {
    activeRequests: number;
    totalRequests: number;
    successRate: number;
    averageResponseTime: number;
  };
}> {
  const startTime = dayjs().valueOf();

  try {
    await apiClient.get('/health', { timeout: 5000 });
    const responseTime = dayjs().diff(startTime);

    const metrics = getIntegrationMetrics();
    const successRate =
      metrics.axios.totalRequests > 0
        ? (metrics.axios.successfulRequests / metrics.axios.totalRequests) * 100
        : 100;

    return {
      status: 'healthy',
      responseTime,
      timestamp: dayjs().toISOString(),
      integration: {
        activeRequests: defaultIntegration.axios.getActiveRequests().length,
        totalRequests: metrics.axios.totalRequests,
        successRate,
        averageResponseTime: metrics.performance.averageResponseTime,
      },
    };
  } catch {
    const responseTime = dayjs().diff(startTime);
    const metrics = getIntegrationMetrics();

    return {
      status: 'unhealthy',
      responseTime,
      timestamp: dayjs().toISOString(),
      integration: {
        activeRequests: defaultIntegration.axios.getActiveRequests().length,
        totalRequests: metrics.axios.totalRequests,
        successRate: 0,
        averageResponseTime: metrics.performance.averageResponseTime,
      },
    };
  }
}

// =============================================================================
// REQUEST ID GENERATOR
// =============================================================================

/**
 * Generates a unique request ID for tracking purposes
 *
 * @returns Unique request ID
 */
export function generateRequestId(): string {
  return `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;
}

// =============================================================================
// CLIENT FACTORY WITH CUSTOM INTEGRATION
// =============================================================================

/**
 * Create a new API client with custom integration configuration
 *
 * @param config - Client and integration configuration
 * @returns Object with configured axios instance, integration layer, and coordinator
 */
export function createApiClientWithCustomIntegration(
  config: ApiClientConfig & { integration: UnifiedIntegrationConfig }
): {
  client: AxiosInstance;
  integration: UnifiedIntegration;
  coordinator: IRequestCoordinator;
} {
  return createApiClient(config);
}

/**
 * Create a minimal API client without integration layer (for special cases)
 *
 * @param config - Basic client configuration
 * @returns Basic axios instance without integration
 */
export function createBasicApiClient(
  config: Omit<ApiClientConfig, 'integration'> = {}
): AxiosInstance {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const client = axios.create({
    baseURL: finalConfig.baseURL,
    timeout: finalConfig.timeout,
    withCredentials: finalConfig.withCredentials,
    withXSRFToken: finalConfig.withXSRFToken,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  // The auth client too: sign-in and registration are requests like any other.
  client.interceptors.request.use(stampTimeZone);

  if (finalConfig.enableLogging) {
    console.log('🔧 Basic API Client created (no integration):', {
      baseURL: finalConfig.baseURL,
      timeout: finalConfig.timeout,
    });
  }

  return client;
}

// =============================================================================
// CLEANUP UTILITIES
// =============================================================================

/**
 * Cleanup the default integration layer
 * Call this when shutting down the application
 */
export function cleanupIntegration(): void {
  defaultIntegration.cleanup();
  console.log('🧹 Default integration cleaned up');
}

/**
 * Reset integration state (useful for testing).
 *
 * Also clears the coordinator's deduplication cache and resets circuit breaker
 * state so a shared client does not carry in-flight/open state across test
 * cases.
 */
export function resetIntegrationState(): void {
  defaultIntegration.performance.clearPerformanceData();
  defaultCoordinator.clearDeduplicationCache();
  defaultCoordinator.resetCircuitBreaker();
}
